/**
 * Admin: Shopify product catalog, subscription offers and checkout branding.
 *
 * Everything money-related is read from the providers, not typed here: the shelf price
 * comes from Shopify and the recurring amount from the selected iyzico pricing plan. The
 * form only decides *which* pricing plan a variant uses and how the card is presented.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, getErrorMessage } from '../lib/api';
import type {
  AdminCatalogProduct,
  AdminCatalogVariant,
  AdminOffer,
  AdminOfferInput,
  Branding,
  IyzicoCatalogPricingPlan,
  PlanCatalog,
} from '../lib/schemas';

function formatTry(minor: number): string {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(minor / 100);
}

function formatDateTime(value: string | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Europe/Istanbul',
  }).format(new Date(value));
}

function stockLabel(variant: AdminCatalogVariant): string {
  if (!variant.availableForSale) return 'Satışa kapalı';
  if (variant.inventoryQuantity === undefined) return 'Takip edilmiyor';
  if (variant.inventoryQuantity <= 0) {
    return variant.inventoryPolicy === 'CONTINUE' ? 'Stok yok (satış açık)' : 'Stok yok';
  }
  return `${variant.inventoryQuantity} adet`;
}

export function AdminProductsPage() {
  const [catalog, setCatalog] = useState<AdminCatalogProduct[]>([]);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [planCatalog, setPlanCatalog] = useState<PlanCatalog | null>(null);
  const [editing, setEditing] = useState<{
    product: AdminCatalogProduct;
    variant: AdminCatalogVariant;
    offer?: AdminOffer;
  } | null>(null);

  const load = useCallback(async (cursor?: string, query?: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.admin.getProductCatalog({
        ...(cursor ? { cursor } : {}),
        ...(query ? { query } : {}),
      });
      setCatalog(result.products);
      setNextCursor(result.nextCursor);
      setLastSyncedAt(result.lastSyncedAt);
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    api.admin
      .getPlanCatalog()
      .then(setPlanCatalog)
      .catch(() => setPlanCatalog(null));
  }, [load]);

  /** Runs one admin mutation, then reloads the current catalog page. */
  async function run(action: () => Promise<unknown>, fallback: string) {
    setStatus(null);
    setError(null);
    try {
      const result = await action();
      const message =
        typeof result === 'object' && result !== null && 'message' in result
          ? String((result as { message: unknown }).message)
          : fallback;
      setStatus(message);
      await load(cursorStack.at(-1), search || undefined);
    } catch (caught) {
      setError(getErrorMessage(caught));
    }
  }

  return (
    <section className="page">
      <header className="page__header">
        <div>
          <h1>Shopify ürünleri ve abonelik planları</h1>
          <p className="page__lead">
            Shopify Admin GraphQL sayfalamasıyla tüm ürünler listelenir. Abonelik fiyatı, Shopify
            fiyatını değiştirmez; fiyat farkı burada uyarı olarak gösterilir.
          </p>
        </div>
        <p className="page__meta">Son senkronizasyon: {formatDateTime(lastSyncedAt)}</p>
      </header>

      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          setCursorStack([]);
          void load(undefined, search || undefined);
        }}
      >
        <label className="field">
          <span>Ürün ara</span>
          <input value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
        <button type="submit">Ara</button>
        <button
          type="button"
          onClick={() => {
            setSearch('');
            setCursorStack([]);
            void load();
          }}
        >
          Shopify’dan yenile
        </button>
      </form>

      {error ? (
        <p role="alert" className="alert alert--critical">
          {error}
        </p>
      ) : null}
      {status ? (
        <p role="status" className="alert alert--success">
          {status}
        </p>
      ) : null}
      {loading ? <p role="status">Ürünler yükleniyor…</p> : null}

      {catalog.map((product) => (
        <article className="card" key={product.shopifyProductGid}>
          <header className="card__header">
            {product.imageUrl ? (
              <img src={product.imageUrl} alt="" width={64} height={64} className="thumb" />
            ) : null}
            <div>
              <h2>{product.title}</h2>
              <p className="muted">
                Ürün ID {product.shopifyProductId} · {product.status ?? '—'} ·{' '}
                {product.activeOfferCount} aktif plan
              </p>
            </div>
            <div className="card__actions">
              <button
                type="button"
                onClick={() =>
                  void run(
                    () =>
                      api.admin.setProductSubscription(
                        product.shopifyProductGid,
                        !product.subscriptionEnabled,
                      ),
                    'Ürün abonelik durumu güncellendi.',
                  )
                }
              >
                {product.subscriptionEnabled ? 'Ürün aboneliğini kapat' : 'Ürün aboneliğini aç'}
              </button>
              <button
                type="button"
                disabled={!product.subscriptionEnabled}
                onClick={() =>
                  void run(
                    () =>
                      api.admin.setProductSubscription(
                        product.shopifyProductGid,
                        product.subscriptionEnabled,
                        !product.subscriptionOnly,
                      ),
                    product.subscriptionOnly
                      ? 'Tek seferlik satın alma yeniden açıldı.'
                      : 'Ürün yalnızca abonelikle satılacak; Sepete Ekle gizlenir.',
                  )
                }
              >
                {product.subscriptionOnly ? 'Tek seferlik satışı aç' : 'Yalnızca abonelik yap'}
              </button>
            </div>
          </header>

          <table className="table">
            <thead>
              <tr>
                <th scope="col">Varyant</th>
                <th scope="col">SKU</th>
                <th scope="col">Shopify fiyatı</th>
                <th scope="col">Stok</th>
                <th scope="col">Abonelik</th>
                <th scope="col">Aktif plan</th>
                <th scope="col">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {product.variants.map((variant) => (
                <>
                  <tr key={variant.shopifyVariantGid}>
                    <th scope="row">
                      {variant.title}
                      <span className="muted"> · {variant.shopifyVariantId}</span>
                    </th>
                    <td>{variant.sku ?? '—'}</td>
                    <td>{formatTry(variant.regularPriceMinor)}</td>
                    <td>{stockLabel(variant)}</td>
                    <td>{variant.subscriptionEnabled ? 'Açık' : 'Kapalı'}</td>
                    <td>{variant.activeOfferCount}</td>
                    <td>
                      <button type="button" onClick={() => setEditing({ product, variant })}>
                        Plan ekle
                      </button>
                      <button
                        type="button"
                        disabled={variant.offers.length === 0}
                        onClick={() =>
                          void run(
                            () =>
                              api.admin.setVariantSubscription(
                                variant.shopifyVariantGid,
                                !variant.subscriptionEnabled,
                              ),
                            'Varyant abonelik durumu güncellendi.',
                          )
                        }
                      >
                        {variant.subscriptionEnabled ? 'Kapat' : 'Aç'}
                      </button>
                    </td>
                  </tr>
                  {variant.offers.map((offer) => (
                    <tr className="row--offer" key={offer.id}>
                      <td colSpan={7}>
                        <div className="offer-row">
                          <div>
                            <strong>{offer.displayTitle}</strong>{' '}
                            <span className="muted">({offer.frequencyLabel})</span>
                            {offer.badgeText ? (
                              <span className="badge">{offer.badgeText}</span>
                            ) : null}
                            {offer.defaultForVariant ? (
                              <span className="badge badge--default">Varsayılan</span>
                            ) : null}
                            {!offer.active ? (
                              <span className="badge badge--muted">Pasif</span>
                            ) : null}
                            <span className="badge badge--muted">
                              Fiyat sürümü v{offer.version}
                            </span>
                            <PriceManager
                              offer={offer}
                              liveRegularMinor={variant.regularPriceMinor}
                              onDone={(message) => {
                                setStatus(message);
                                void load(cursorStack.at(-1), search || undefined);
                              }}
                              onError={setError}
                            />
                          </div>
                          <div className="card__actions">
                            <button
                              type="button"
                              onClick={() => setEditing({ product, variant, offer })}
                            >
                              Düzenle
                            </button>
                            <button
                              type="button"
                              disabled={offer.defaultForVariant || !offer.active}
                              onClick={() =>
                                void run(
                                  async () =>
                                    api.admin.setOfferState(offer.id, {
                                      defaultForVariant: true,
                                    }),
                                  'Varsayılan plan güncellendi.',
                                )
                              }
                            >
                              Varsayılan yap
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                void run(
                                  async () =>
                                    api.admin.setOfferState(offer.id, { active: !offer.active }),
                                  'Plan durumu güncellendi.',
                                )
                              }
                            >
                              {offer.active ? 'Pasif yap' : 'Etkinleştir'}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </article>
      ))}

      <nav className="pagination">
        <button
          type="button"
          disabled={cursorStack.length === 0}
          onClick={() => {
            const stack = cursorStack.slice(0, -1);
            setCursorStack(stack);
            void load(stack.at(-1), search || undefined);
          }}
        >
          Önceki sayfa
        </button>
        <button
          type="button"
          disabled={nextCursor === undefined}
          onClick={() => {
            if (nextCursor === undefined) return;
            const stack = [...cursorStack, nextCursor];
            setCursorStack(stack);
            void load(nextCursor, search || undefined);
          }}
        >
          Sonraki sayfa
        </button>
      </nav>

      {editing ? (
        <OfferEditor
          product={editing.product}
          variant={editing.variant}
          {...(editing.offer ? { offer: editing.offer } : {})}
          pricingPlans={planCatalog?.iyzicoPricingPlans ?? []}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null);
            setStatus(message);
            void load(cursorStack.at(-1), search || undefined);
          }}
        />
      ) : null}
    </section>
  );
}

const QUICK_DISCOUNTS = [5, 10, 15, 20] as const;

/**
 * Inline price management for one offer.
 *
 * The admin works only in TL: quick-discount buttons compute the sale price from the
 * live Shopify shelf price, the custom field takes an exact amount, and "İndirimi
 * kaldır" returns to the shelf price. One call to the server creates the matching
 * iyzico pricing plan behind the scenes and supersedes the offer — nothing
 * provider-specific is ever typed here.
 */
function PriceManager(props: {
  offer: AdminOffer;
  liveRegularMinor: number;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}) {
  const { offer } = props;
  const [priceText, setPriceText] = useState('');
  const [busy, setBusy] = useState(false);
  const regular = props.liveRegularMinor > 0 ? props.liveRegularMinor : offer.regularPriceMinor;
  const shelfOutOfSync = offer.regularPriceMinor !== regular || offer.priceDrift !== undefined;

  async function apply(newMinor: number, label: string) {
    if (busy) return;
    setBusy(true);
    try {
      const result = await api.admin.repriceOffer(offer.id, {
        expectedVersion: offer.version,
        newSubscriptionPriceMinor: newMinor,
      });
      props.onDone(
        `${label}: yeni tahsilat tutarı ${formatTry(result.offer.subscriptionPriceMinor)}. ` +
          (result.providerPlanCreated !== undefined
            ? 'iyzico fiyat planı otomatik oluşturuldu; yeni abonelikler bu fiyatla başlar.'
            : 'Fiyat zaten güncel.'),
      );
    } catch (caught) {
      props.onError(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function syncShelf() {
    if (busy) return;
    setBusy(true);
    try {
      const result = await api.admin.syncOfferShelfPrice(offer.id);
      props.onDone(
        `Shopify fiyatı eşitlendi: normal fiyat ${formatTry(result.offer.regularPriceMinor)}.`,
      );
    } catch (caught) {
      props.onError(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  function applyCustom() {
    const normalized = priceText.replace(',', '.').trim();
    const value = Number(normalized);
    if (!Number.isFinite(value) || value < 1) {
      props.onError('Geçerli bir fiyat girin (en az 1 TL).');
      return;
    }
    void apply(Math.round(value * 100), 'Özel fiyat uygulandı');
  }

  return (
    <div className="price-manager">
      <dl className="price-manager__figures">
        <div>
          <dt>Normal fiyat</dt>
          <dd>{formatTry(regular)}</dd>
        </div>
        <div>
          <dt>Aktif satış fiyatı</dt>
          <dd className="price-manager__active">{formatTry(offer.subscriptionPriceMinor)}</dd>
        </div>
        <div>
          <dt>İndirim</dt>
          <dd>
            {offer.discountAmountMinor > 0
              ? `%${offer.discountPercentage} (−${formatTry(offer.discountAmountMinor)})`
              : 'Yok'}
          </dd>
        </div>
      </dl>
      {shelfOutOfSync ? (
        <p className="alert alert--warning">
          Shopify fiyatı değişti ({formatTry(offer.regularPriceMinor)} → {formatTry(regular)}).
          Tahsilat tutarı otomatik değişmez.{' '}
          <button type="button" disabled={busy} onClick={() => void syncShelf()}>
            Normal fiyatı Shopify ile eşitle
          </button>
        </p>
      ) : null}
      <div className="price-manager__quick">
        <span className="muted">Hızlı indirim:</span>
        {QUICK_DISCOUNTS.map((percent) => (
          <button
            key={percent}
            type="button"
            disabled={busy}
            onClick={() =>
              void apply(Math.round(regular * (1 - percent / 100)), `%${percent} indirim uygulandı`)
            }
          >
            %{percent}
          </button>
        ))}
        <button
          type="button"
          disabled={busy || offer.subscriptionPriceMinor === regular}
          onClick={() => void apply(regular, 'İndirim kaldırıldı, normal fiyata dönüldü')}
        >
          İndirimi kaldır
        </button>
        <label className="price-manager__custom">
          <span className="muted">Özel fiyat (TL)</span>
          <input
            inputMode="decimal"
            placeholder={(offer.subscriptionPriceMinor / 100).toFixed(2)}
            value={priceText}
            onChange={(event) => setPriceText(event.target.value)}
          />
        </label>
        <button type="button" disabled={busy || priceText.trim() === ''} onClick={applyCustom}>
          Fiyatı uygula
        </button>
        {busy ? <span className="muted">Uygulanıyor…</span> : null}
      </div>
      <p className="muted price-manager__note">
        Fiyat değişikliği yalnızca yeni abonelikleri etkiler; mevcut abonelerin tahsilatı değişmez.
        Ödeme sistemi eşlemesi otomatik yapılır.
      </p>
    </div>
  );
}

function OfferEditor(props: {
  product: AdminCatalogProduct;
  variant: AdminCatalogVariant;
  offer?: AdminOffer;
  pricingPlans: IyzicoCatalogPricingPlan[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { offer, variant, product } = props;
  const [frequencyUnit, setFrequencyUnit] = useState<'WEEK' | 'MONTH'>(
    offer?.frequencyUnit ?? 'MONTH',
  );
  const [intervalCount, setIntervalCount] = useState(offer?.intervalCount ?? 1);
  const [pricingPlanReference, setPricingPlanReference] = useState(
    offer?.iyzicoPricingPlanReferenceCode ?? '',
  );
  const [displayTitle, setDisplayTitle] = useState(offer?.displayTitle ?? '');
  const [displayDescription, setDisplayDescription] = useState(offer?.displayDescription ?? '');
  const [badgeText, setBadgeText] = useState(offer?.badgeText ?? '');
  const [sortOrder, setSortOrder] = useState(offer?.sortOrder ?? 0);
  const [active, setActive] = useState(offer?.active ?? true);
  const [defaultForVariant, setDefaultForVariant] = useState(offer?.defaultForVariant ?? false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const selectedPlan = useMemo(
    () => props.pricingPlans.find((plan) => plan.referenceCode === pricingPlanReference),
    [props.pricingPlans, pricingPlanReference],
  );

  const subscriptionPriceMinor = selectedPlan?.amountMinor ?? 0;
  const discountMinor = Math.max(0, variant.regularPriceMinor - subscriptionPriceMinor);
  const discountPercentage =
    variant.regularPriceMinor === 0
      ? 0
      : Math.round((discountMinor / variant.regularPriceMinor) * 100);
  const repricing =
    offer !== undefined && offer.iyzicoPricingPlanReferenceCode !== pricingPlanReference;

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    if (selectedPlan === undefined) {
      setError('Bir iyzico pricing plan seçin.');
      return;
    }
    setSaving(true);
    try {
      const input: AdminOfferInput = {
        ...(offer ? { offerId: offer.id, expectedVersion: offer.version } : {}),
        shopifyProductGid: product.shopifyProductGid,
        shopifyVariantGid: variant.shopifyVariantGid,
        frequencyUnit,
        intervalCount,
        ...(displayTitle.trim() ? { displayTitle: displayTitle.trim() } : {}),
        ...(displayDescription.trim() ? { displayDescription: displayDescription.trim() } : {}),
        ...(badgeText.trim() ? { badgeText: badgeText.trim() } : {}),
        subscriptionPriceMinor: selectedPlan.amountMinor,
        shippingPriceMinor: offer?.shippingPriceMinor ?? 0,
        iyzicoProductReferenceCode: selectedPlan.productReferenceCode,
        iyzicoPricingPlanReferenceCode: selectedPlan.referenceCode,
        active,
        defaultForVariant,
        sortOrder,
      };
      const result = await api.admin.upsertOffer(input);
      props.onSaved(
        result.repricedFrom
          ? 'Yeni fiyatlı plan oluşturuldu. Eski plan yeni aboneler için pasifleştirildi; mevcut aboneler eski planda kaldı.'
          : 'Abonelik planı kaydedildi.',
      );
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Abonelik planı">
      <form className="modal__panel" onSubmit={save}>
        <h2>
          {offer ? 'Planı düzenle' : 'Plan ekle'} — {product.title} / {variant.title}
        </h2>
        <p className="muted">
          Shopify normal fiyatı {formatTry(variant.regularPriceMinor)}. Abonelik fiyatı seçtiğiniz
          iyzico pricing plan tarafından belirlenir.
        </p>

        <div className="grid-2">
          <label className="field">
            <span>Teslimat sıklığı birimi</span>
            <select
              value={frequencyUnit}
              onChange={(event) => setFrequencyUnit(event.target.value as 'WEEK' | 'MONTH')}
            >
              <option value="WEEK">Hafta</option>
              <option value="MONTH">Ay</option>
            </select>
          </label>
          <label className="field">
            <span>Aralık</span>
            <input
              type="number"
              min={1}
              max={52}
              value={intervalCount}
              onChange={(event) => setIntervalCount(Number(event.target.value))}
            />
          </label>
        </div>

        <label className="field">
          <span>iyzico pricing plan</span>
          <select
            value={pricingPlanReference}
            onChange={(event) => setPricingPlanReference(event.target.value)}
          >
            <option value="">Seçin…</option>
            {props.pricingPlans.map((plan) => (
              <option key={plan.referenceCode} value={plan.referenceCode}>
                {plan.name} — {formatTry(plan.amountMinor)} / {plan.paymentIntervalCount}{' '}
                {plan.paymentInterval}
              </option>
            ))}
          </select>
        </label>

        {selectedPlan ? (
          <p className="muted">
            Abonelik fiyatı {formatTry(subscriptionPriceMinor)}
            {discountMinor > 0
              ? ` · %${discountPercentage} indirim · ${formatTry(discountMinor)} tasarruf`
              : ' · indirim yok'}
          </p>
        ) : null}

        {repricing ? (
          <p className="alert alert--warning">
            Pricing plan değişiyor. Mevcut iyzico planı değiştirilmez: yeni bir plan sürümü
            oluşturulur, eski plan yeni aboneler için pasifleşir ve mevcut aboneler eski planda
            kalır.
          </p>
        ) : null}

        <div className="grid-2">
          <label className="field">
            <span>Kart başlığı</span>
            <input
              value={displayTitle}
              placeholder="Örn. Ayda bir"
              onChange={(event) => setDisplayTitle(event.target.value)}
            />
          </label>
          <label className="field">
            <span>Badge</span>
            <input
              value={badgeText}
              placeholder="Örn. En popüler"
              onChange={(event) => setBadgeText(event.target.value)}
            />
          </label>
        </div>

        <label className="field">
          <span>Açıklama</span>
          <textarea
            value={displayDescription}
            rows={2}
            onChange={(event) => setDisplayDescription(event.target.value)}
          />
        </label>

        <div className="grid-2">
          <label className="field">
            <span>Sıralama</span>
            <input
              type="number"
              min={0}
              max={10000}
              value={sortOrder}
              onChange={(event) => setSortOrder(Number(event.target.value))}
            />
          </label>
          <div className="field">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={active}
                onChange={(event) => setActive(event.target.checked)}
              />
              <span>Aktif</span>
            </label>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={defaultForVariant}
                onChange={(event) => setDefaultForVariant(event.target.checked)}
              />
              <span>Bu varyantın varsayılan planı</span>
            </label>
          </div>
        </div>

        {error ? (
          <p role="alert" className="alert alert--critical">
            {error}
          </p>
        ) : null}

        <div className="card__actions">
          <button type="submit" disabled={saving}>
            {saving ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
          <button type="button" onClick={props.onClose}>
            Vazgeç
          </button>
        </div>
      </form>
    </div>
  );
}

const BRANDING_FIELDS: ReadonlyArray<{ key: keyof Branding; label: string; type: string }> = [
  { key: 'logoUrl', label: 'Logo URL', type: 'url' },
  { key: 'logoAlt', label: 'Logo alternatif metni', type: 'text' },
  { key: 'faviconUrl', label: 'Favicon URL', type: 'url' },
  { key: 'primaryColor', label: 'Ana renk', type: 'color' },
  { key: 'primaryHoverColor', label: 'Ana renk (hover)', type: 'color' },
  { key: 'primaryTextColor', label: 'Buton yazı rengi', type: 'color' },
  { key: 'backgroundColor', label: 'Arka plan', type: 'color' },
  { key: 'surfaceColor', label: 'Yüzey', type: 'color' },
  { key: 'surfaceMutedColor', label: 'Yüzey (soluk)', type: 'color' },
  { key: 'textColor', label: 'Metin', type: 'color' },
  { key: 'mutedTextColor', label: 'Metin (soluk)', type: 'color' },
  { key: 'headingColor', label: 'Başlık', type: 'color' },
  { key: 'borderColor', label: 'Kenarlık', type: 'color' },
  { key: 'successColor', label: 'Başarı', type: 'color' },
  { key: 'criticalColor', label: 'Hata', type: 'color' },
  { key: 'bodyFontFamily', label: 'Gövde fontu', type: 'text' },
  { key: 'headingFontFamily', label: 'Başlık fontu', type: 'text' },
  { key: 'buttonRadius', label: 'Buton radius', type: 'text' },
  { key: 'cardRadius', label: 'Kart radius', type: 'text' },
  { key: 'inputRadius', label: 'Input radius', type: 'text' },
  { key: 'badgeRadius', label: 'Badge radius', type: 'text' },
  { key: 'containerWidth', label: 'Container genişliği', type: 'text' },
];

export function AdminBrandingPage() {
  const [branding, setBranding] = useState<Branding | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.admin
      .getBranding()
      .then(setBranding)
      .catch((caught: unknown) => setError(getErrorMessage(caught)));
  }, []);

  if (branding === null) {
    return (
      <section className="page">
        <h1>Marka ve Checkout Tasarımı</h1>
        {error ? (
          <p role="alert" className="alert alert--critical">
            {error}
          </p>
        ) : (
          <p>Yükleniyor…</p>
        )}
      </section>
    );
  }

  function update(key: keyof Branding, value: string) {
    setBranding((current) => (current === null ? current : { ...current, [key]: value }));
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (branding === null) return;
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      setBranding(await api.admin.updateBranding(branding));
      setStatus('Checkout tasarımı kaydedildi.');
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  async function syncTheme() {
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      const result = await api.admin.syncBrandingFromTheme();
      setBranding(result.branding);
      setStatus(
        result.appliedFields.length === 0
          ? 'Temadan yeni bir değer bulunamadı; mevcut ayarlar korundu.'
          : `Temadan güncellenen alanlar: ${result.appliedFields.join(', ')}`,
      );
    } catch (caught) {
      // A failed sync must never wipe working branding, so nothing is written locally.
      setError(getErrorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="page">
      <header className="page__header">
        <div>
          <h1>Marka ve Checkout Tasarımı</h1>
          <p className="page__lead">
            Checkout sayfası bu değerleri CSS değişkeni olarak kullanır. “Temadan senkronize et”
            yalnızca herkese açık storefront HTML’inden tasarım değişkenlerini okur; ek Shopify izni
            istemez.
          </p>
        </div>
      </header>

      {error ? (
        <p role="alert" className="alert alert--critical">
          {error}
        </p>
      ) : null}
      {status ? (
        <p role="status" className="alert alert--success">
          {status}
        </p>
      ) : null}

      <form onSubmit={save}>
        <div className="grid-2">
          {BRANDING_FIELDS.map((field) => (
            <label className="field" key={field.key}>
              <span>{field.label}</span>
              <input
                type={field.type === 'color' ? 'text' : field.type}
                value={branding[field.key] ?? ''}
                onChange={(event) => update(field.key, event.target.value)}
              />
            </label>
          ))}
        </div>

        <div className="card__actions">
          <button type="submit" disabled={saving}>
            {saving ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
          <button type="button" disabled={saving} onClick={() => void syncTheme()}>
            Shopify temasından senkronize et
          </button>
        </div>
      </form>

      <section className="card" aria-label="Checkout önizlemesi">
        <h2>Checkout önizlemesi</h2>
        <div
          className="branding-preview"
          style={
            {
              background: branding.backgroundColor,
              color: branding.textColor,
              fontFamily: branding.bodyFontFamily,
              border: `1px solid ${branding.borderColor}`,
              borderRadius: branding.cardRadius,
              padding: '1.5rem',
            } as React.CSSProperties
          }
        >
          <img src={branding.logoUrl} alt={branding.logoAlt} height={40} />
          <h3 style={{ color: branding.headingColor, fontFamily: branding.headingFontFamily }}>
            Abonelik onayı
          </h3>
          <p style={{ color: branding.mutedTextColor }}>Ayda bir · Kargo dahil</p>
          <button
            type="button"
            style={
              {
                background: branding.primaryColor,
                color: branding.primaryTextColor,
                border: 'none',
                borderRadius: branding.buttonRadius,
                padding: '16px 24px',
                fontWeight: 650,
              } as React.CSSProperties
            }
          >
            Aboneliği Başlat ve Öde
          </button>
        </div>
      </section>
    </section>
  );
}
